import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Stack,
  alpha,
  Divider,
} from '@mui/material';
import { getSamplePointRecord, sendSamplePointRecord } from '../../../../helpers/api';
import { useEffect, useState } from 'react';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { Empty } from 'antd';
import { parseString4 } from '../../../../helpers/utils';
import { openNotification } from '../../../../helpers/notification';
import { reportStatuses } from '../../../../helpers/appstate';
import { EditOutlined, SaveOutlined, TuneOutlined } from '@mui/icons-material';

export default function ResultViewMeasurementsParameterInput({ report, samplePoint, onRefresh }) {
  const [samplePointParameter, setSamplePointParameter] = useState();
  const [samplePointParemeterLoading, setSamplePointParameterLoading] = useState(false);
  const [inputList, setInputList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [isReadView, setIsReadView] = useState(true);

  useEffect(() => {
    if (samplePoint) fetchSamplePointParameters();
  }, [samplePoint]);

  useEffect(() => {
    if (refresh) fetchSamplePointParameters();
  }, [refresh]);

  const fetchSamplePointParameters = async () => {
    setSamplePointParameterLoading(true);
    try {
      const res = await getSamplePointRecord(samplePoint?.samplePointId, report?.reportId);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setSamplePointParameter(data);
        const flattenResultList = data?.results?.flatMap((i) => i.data) || [];
        let initInputList = [];

        data?.parameters?.forEach((i) => {
          const result = flattenResultList?.find((x) => x?.parameterId === i?.parameterId);
          const resultValue = !isNaN(result?.resultValue) ? result?.resultValue.toFixed(2) : '';
          initInputList.push({
            parameterId: i?.parameterId,
            unit: i?.unit,
            resultValue: resultValue,
          });
        });
        setInputList(initInputList);

        const computedCount = initInputList?.filter((x) => x?.resultValue !== '')?.length;
        setIsReadView(computedCount === data?.parameters?.length);
      }
    } catch (error) {
      openNotification({ type: 'error', message: error?.data?.status?.desc || 'Fetch failed' });
    } finally {
      setSamplePointParameterLoading(false);
      setRefresh(false);
    }
  };

  const handleInputChange = (newValue, itemToUpdate) => {
    setInputList((prev) =>
      prev.map((item) =>
        item.parameterId === itemToUpdate.parameterId ? { ...item, resultValue: newValue } : item
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const measurements = inputList
        ?.filter((x) => x?.resultValue !== '')
        ?.map((i) => ({
          parameterId: i?.parameterId,
          value: Number(i?.resultValue),
          entryDate: new Date().toISOString(),
        }));

      if (measurements?.length !== inputList?.length) {
        openNotification({ type: 'error', message: 'All necessary parameters must be filled' });
        return;
      }

      const res = await sendSamplePointRecord({
        reportId: report?.reportId,
        facilityId: report?.facilityId,
        samplePointId: samplePoint.samplePointId,
        measurmentId: samplePoint?.measurementId,
        measurements: [measurements],
      });

      if (res?.result?.status?.code === '00') {
        openNotification({ type: 'success', message: res.result.status.desc });
        setTimeout(() => {
          setRefresh(true);
          onRefresh(samplePoint);
        }, 1000);
      } else {
        openNotification({ type: 'error', message: res?.result?.status?.desc });
      }
    } catch (error) {
      openNotification({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const renderInputView = () => (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gap: 2,
          mb: 3,
        }}
      >
        {inputList?.map((i) => (
          <TextField
            key={i?.parameterId}
            label={parseString4(i?.unit)}
            value={i?.resultValue || ''}
            type="number"
            size="small"
            onChange={(e) => handleInputChange(e.target.value, i)}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fcfcfc' },
            }}
          />
        ))}
      </Box>

      {(report?.reportStatus === reportStatuses.DRAFT ||
        report?.reportStatus === reportStatuses.REJECTED) && (
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveOutlined />}
          onClick={handleSave}
          disabled={saving}
          sx={{ borderRadius: '10px', textTransform: 'none', px: 4, fontWeight: 700 }}
        >
          {saving ? 'Saving...' : 'Save Measurements'}
        </Button>
      )}
    </Box>
  );

  const renderReadView = () => (
    <Box>
      <TableContainer
        component={Box}
        sx={{
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Table size="small">
          <TableHead sx={{ bgcolor: alpha('#1976d2', 0.05) }}>
            <TableRow>
              {inputList?.map((header, idx) => (
                <TableCell
                  key={idx}
                  sx={{ borderRight: '1px solid', borderColor: 'divider', py: 1.5 }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="primary"
                    dangerouslySetInnerHTML={{ __html: header?.unit }}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {inputList?.map((body) => {
                const val = Number(body?.resultValue);
                const displayVal = !isNaN(val) ? val.toFixed(2) : body?.resultValue;
                return (
                  <TableCell
                    key={body?.parameterId}
                    sx={{
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      textAlign: 'right',
                      py: 2,
                    }}
                  >
                    <Typography variant="body2" fontWeight={700} color="text.primary">
                      {displayVal || '-'}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {(report?.reportStatus === reportStatuses.DRAFT ||
        report?.reportStatus === reportStatuses.REJECTED) && (
        <Button
          variant="outlined"
          startIcon={<EditOutlined />}
          onClick={() => setIsReadView(false)}
          sx={{ mt: 3, borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
        >
          Modify Data
        </Button>
      )}
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fff',
      }}
    >
      {samplePointParemeterLoading ? (
        <Box sx={{ p: 2 }}>
          <LoaderSkeleton />
        </Box>
      ) : !samplePointParameter ? (
        <Empty description="No parameters found for this sample point" />
      ) : (
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
            <TuneOutlined color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {samplePointParameter?.samplePointName}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 1 }}
              >
                {samplePointParameter?.groupName}
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ mb: 3 }} />
          {isReadView ? renderReadView() : renderInputView()}
        </Box>
      )}
    </Paper>
  );
}
